import { useState } from "react";
import type { ChangeEvent } from "react";
import type { WordStyles as Template } from "../menuDocx.ts";
import { api, messageOf, store, useStore } from "../store.ts";

/**
 * „Word“ in a style, as in KellerLog: download a menu as Word, change its styles there (font,
 * sizes, colours, spacing, tab stops, page), upload it again; every Word file of a menu in this
 * style then carries them, until „Wieder aus dem Stil“. One template per style (S21): the file
 * says which style made it, and a file of another style asks where it belongs.
 */
export function WordStyles({ style, styles }: { style: { id: string; name: string }; styles: { id: string; name: string }[] }) {
  const s = useStore();
  const word = s.data?.word.find((w) => w.style === style.id) ?? null;
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  /** A file made in another style: for that one or for this one? */
  const [ask, setAsk] = useState<{ template: Template; from: { id: string; name: string } } | null>(null);

  async function put(template: Template, to: { id: string; name: string }) {
    setAsk(null);
    try {
      await store.change(() => api(`/api/word-styles/${encodeURIComponent(to.id)}`, { method: "PUT", body: template }));
      setState({ ok: `Formatvorlage für den Stil „${to.name}“ übernommen. Jede Word-Datei einer Speisekarte in diesem Stil bekommt sie.` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  async function upload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setState({ busy: true });
    try {
      const { readWordStyles } = await import("../menuDocx.ts");
      const read = await readWordStyles(file, file.name);
      if ("error" in read) return setState({ error: read.error });
      const { source, ...template } = read;
      const from = source && source.id !== style.id ? styles.find((x) => x.id === source.id) : undefined;
      setState({});
      if (from) setAsk({ template, from });
      else await put(template, style);
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  async function reset() {
    if (!confirm(`Die eigene Formatvorlage des Stils „${style.name}“ löschen? Word nimmt dann wieder Schriften und Abstände des Stils.`)) return;
    try {
      await store.change(() => api(`/api/word-styles/${encodeURIComponent(style.id)}`, { method: "DELETE" }));
      setState({ ok: "Word nimmt wieder den Stil." });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  return (
    <section className="card word-styles" style={{ marginTop: "1rem" }} data-testid="word-styles">
      <details>
        <summary>
          Eigene Word-Vorlage für „{style.name}“{word ? `: ${word.file}` : ""}
        </summary>
        <p className="hint">Ohne eigene Vorlage nimmt Word Schriften, Größen, Farben und Abstände dieses Stils. Wer in Word mehr bestimmen will:</p>
        <p className="hint">
          Eine Speisekarte in diesem Stil als Word-Datei herunterladen, in Word die Formatvorlagen ändern („Speise“, „Beschreibung“, „Abschnitt“, „Preis“ …, auch Schrift, Ränder und Tabstopps), speichern und hier hochladen. Behalten werden nur die Formatvorlagen, das Design und die Seite, nicht der Inhalt. Die Datei weiß, aus welchem Stil sie kommt.
        </p>
        <p className="hint">Ein Logo nicht in die Word-Datei setzen, sondern hier oben unter „Logo ändern“ hochladen: Dann steht es auf jeder Speisekarte, auch in Word (Formatvorlage „Logo“).</p>
        <label className="upload">
          <span className="button">{word ? "Andere Word-Datei hochladen …" : "Word-Datei hochladen …"}</span>
          <input type="file" accept=".docx" onChange={upload} hidden data-testid="word-upload" />
        </label>
        {ask && (
          <div className="word-ask" data-testid="word-ask">
            <p>Diese Datei stammt aus dem Stil „{ask.from.name}“. Für welchen Stil ist die Vorlage?</p>
            <div className="buttons">
              <button className="primary" onClick={() => void put(ask.template, ask.from)} data-testid="word-to-source">
                Für „{ask.from.name}“
              </button>
              <button onClick={() => void put(ask.template, style)} data-testid="word-to-this">
                Für „{style.name}“
              </button>
              <button className="link" onClick={() => setAsk(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        )}
        {word && (
          <>
            <p className="muted small">
              Eigene Formatvorlage aus „{word.file}“ vom {new Date(word.at).toLocaleDateString("de-DE")}. Schriften und Abstände in Word können dann vom PDF abweichen.
            </p>
            <button onClick={() => void reset()} data-testid="word-reset">
              Wieder aus dem Stil
            </button>
          </>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && <p className="ok-text">{state.ok}</p>}
      </details>
    </section>
  );
}
